import * as turf from '@turf/turf';
import type { Feature, MultiPolygon, Polygon } from 'geojson';

import { getDemoStormScales } from './floodDemoStormEvent';
import { FLOOD_GRID_CELL_SIZE_M } from './floodDepthScale';
import { calibrateFloodRegionFromGeoJsonProperties } from './floodRegionDisplayCalibration';
import { getFloodScrubberNow } from './floodScrubberTimeline';
import {
  SYNTHETIC_GRID_CELLS_FACTOR,
  SYNTHETIC_GRID_MAX_CELLS_PER_SIDE,
} from './floodSyntheticGridLayout';
import {
  type FloodCalibrationZoneRegion,
  getDepthScaleForYear,
  getNationalFloodIntensityFactor,
  getRegionWeightForYear,
} from './vietnamFloodScenarioCalibration';

/**
 * Deterministic seeded random number generator (Mulberry32).
 * Same seed → same sequence every time → fake data is stable per year.
 */
function seededRandom(seed: number) {
  let s = seed;
  return () => {
    s |= 0;
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Vietnam bounding box + coastal waters for events
const REGIONS = {
  earthquake: [
    { lat: 22.39, lng: 103.15, name: 'Lai Châu – Điện Biên' },
    { lat: 21.38, lng: 103.02, name: 'Điện Biên Phủ' },
    { lat: 21.03, lng: 105.85, name: 'Hà Nội' },
    { lat: 21.32, lng: 103.9, name: 'Sơn La' },
    { lat: 16.06, lng: 108.22, name: 'Đà Nẵng' },
    { lat: 15.12, lng: 108.8, name: 'Quảng Ngãi' },
    { lat: 14.34, lng: 108.0, name: 'Kon Tum' },
    { lat: 13.78, lng: 109.22, name: 'Bình Định' },
    { lat: 12.23, lng: 109.5, name: 'Nha Trang ngoài khơi' },
    { lat: 10.82, lng: 106.66, name: 'TP. Hồ Chí Minh' },
    { lat: 18.5, lng: 112.5, name: 'Bắc Biển Đông' },
    { lat: 16.75, lng: 108.3, name: 'Ngoài khơi Quảng Trị' },
  ],
  tsunami: [
    { lat: 14.5, lng: 114.2, source: 'Vùng hút chìm Manila' },
    { lat: 16.1, lng: 111.8, source: 'Đứt gãy Biển Đông' },
    { lat: 12.3, lng: 117.5, source: 'Ngoài khơi Philippines' },
    { lat: 10.8, lng: 113.5, source: 'Rãnh Palawan' },
    { lat: 8.5, lng: 119.2, source: 'Vùng hội tụ Sulawesi' },
    { lat: 19.5, lng: 116.0, source: 'Đứt gãy Ryukyu' },
    { lat: 6.0, lng: 109.5, source: 'Vùng hút chìm Java' },
  ],
};

const TSUNAMI_STATUS = ['Theo dõi', 'Cảnh báo cao', 'Cảnh báo rất cao'] as const;

export function generateEarthquakeData(year: number) {
  const rand = seededRandom(year * 31337);
  const count = 6 + Math.floor(rand() * 7); // 6–12 events per year
  const features = [];

  for (let i = 0; i < count; i++) {
    const region = REGIONS.earthquake[Math.floor(rand() * REGIONS.earthquake.length)];
    const latJitter = (rand() - 0.5) * 1.5;
    const lngJitter = (rand() - 0.5) * 1.5;
    const mag = 3.0 + rand() * 5.0; // M3.0–M8.0
    const depth = 5 + Math.floor(rand() * 40);

    features.push({
      type: 'Feature',
      properties: {
        mag: Math.round(mag * 10) / 10,
        place: region.name,
        depth_km: depth,
        time: `${year}-${String(Math.floor(rand() * 12) + 1).padStart(2, '0')}-${String(Math.floor(rand() * 28) + 1).padStart(2, '0')}T${String(Math.floor(rand() * 24)).padStart(2, '0')}:00:00Z`,
      },
      geometry: {
        type: 'Point',
        coordinates: [
          Math.round((region.lng + lngJitter) * 10000) / 10000,
          Math.round((region.lat + latJitter) * 10000) / 10000,
        ],
      },
    });
  }

  return { type: 'FeatureCollection', features };
}

export function generateTsunamiData(year: number) {
  const rand = seededRandom(year * 99991 + 7);
  const count = 3 + Math.floor(rand() * 5); // 3–7 warnings per year
  const features = [];

  for (let i = 0; i < count; i++) {
    const src = REGIONS.tsunami[Math.floor(rand() * REGIONS.tsunami.length)];
    const latJitter = (rand() - 0.5) * 2;
    const lngJitter = (rand() - 0.5) * 2;
    const waveH = Math.round((0.5 + rand() * 12) * 10) / 10;
    const statusIdx = waveH > 7 ? 2 : waveH > 3 ? 1 : 0;

    features.push({
      type: 'Feature',
      properties: {
        status: TSUNAMI_STATUS[statusIdx],
        wave_height_m: waveH,
        eta_min: Math.floor(15 + rand() * 120),
        source: src.source,
      },
      geometry: {
        type: 'Point',
        coordinates: [
          Math.round((src.lng + lngJitter) * 10000) / 10000,
          Math.round((src.lat + latJitter) * 10000) / 10000,
        ],
      },
    });
  }

  return { type: 'FeatureCollection', features };
}

// ---------------------------------------------------------------------------
// AI Flood Grid Analysis (lưới ô theo {@link FLOOD_GRID_CELL_SIZE_M} — đồng bộ AlphaEarth / phân tích)
// ---------------------------------------------------------------------------

export const TERRAIN_TYPES = [
  'urban',
  'agricultural',
  'wetland',
  'forest',
  'road',
  'water_body',
] as const;

export type TerrainType = (typeof TERRAIN_TYPES)[number];

export interface FloodCell {
  position: [number, number];
  depth_m: number;
  velocity_ms: number;
  risk_level: 1 | 2 | 3 | 4 | 5;
  terrain: TerrainType;
  confidence: number;
  elevation_m: number;
  zone_name: string;
}

interface FloodGridZone {
  name: string;
  center: [number, number];
  /** Miền căn chỉnh theo kịch bản năm. */
  region: FloodCalibrationZoneRegion;
  gridCells: number;
  floodIntensity: number;
  maxDepth: number;
  terrainWeights: number[];
}

const FLOOD_GRID_ZONES: FloodGridZone[] = [
  // Hà Nội
  {
    name: 'Ngã tư Chùa Bộc - Thái Hà',
    center: [105.8285, 21.0116],
    region: 'north',
    gridCells: 60,
    floodIntensity: 0.9,
    maxDepth: 1.2,
    terrainWeights: [0.6, 0.2, 0.05, 0.05, 0.1, 0.0],
  },
  {
    name: 'Phố Nguyễn Khuyến',
    center: [105.8402, 21.0305],
    region: 'north',
    gridCells: 50,
    floodIntensity: 0.85,
    maxDepth: 1.5,
    terrainWeights: [0.5, 0.2, 0.05, 0.05, 0.15, 0.05],
  },
  {
    name: 'Ngã tư Sở',
    center: [105.8211, 21.0016],
    region: 'north',
    gridCells: 60,
    floodIntensity: 0.8,
    maxDepth: 1.0,
    terrainWeights: [0.6, 0.1, 0.05, 0.05, 0.2, 0.0],
  },
  {
    name: 'Khu vực Cầu Giấy',
    center: [105.7995, 21.0345],
    region: 'north',
    gridCells: 52,
    floodIntensity: 0.82,
    maxDepth: 1.1,
    terrainWeights: [0.55, 0.15, 0.05, 0.05, 0.15, 0.05],
  },
  {
    name: 'Long Biên – Bồ Đề',
    center: [105.895, 21.048],
    region: 'north',
    gridCells: 48,
    floodIntensity: 0.78,
    maxDepth: 1.0,
    terrainWeights: [0.45, 0.15, 0.1, 0.05, 0.2, 0.05],
  },
  {
    name: 'Nam Từ Liêm – Mỹ Đình',
    center: [105.764, 21.0065],
    region: 'north',
    gridCells: 50,
    floodIntensity: 0.8,
    maxDepth: 1.15,
    terrainWeights: [0.58, 0.12, 0.05, 0.05, 0.15, 0.05],
  },
  {
    name: 'Hoàn Kiếm – Phố cổ',
    center: [105.852, 21.0285],
    region: 'north',
    gridCells: 44,
    floodIntensity: 0.75,
    maxDepth: 0.85,
    terrainWeights: [0.65, 0.1, 0.0, 0.0, 0.2, 0.05],
  },
  {
    name: 'Trung Hoà – Nhân Chính',
    center: [105.793, 21.002],
    region: 'north',
    gridCells: 48,
    floodIntensity: 0.83,
    maxDepth: 1.05,
    terrainWeights: [0.52, 0.18, 0.05, 0.05, 0.15, 0.05],
  },
  {
    name: 'Ven sông Hương – Huế',
    center: [107.59, 16.463],
    region: 'central',
    gridCells: 44,
    floodIntensity: 0.84,
    maxDepth: 1.15,
    terrainWeights: [0.45, 0.2, 0.12, 0.05, 0.12, 0.06],
  },
  {
    name: 'Trũng nước nội thành Đà Nẵng',
    center: [108.202, 16.061],
    region: 'central',
    gridCells: 46,
    floodIntensity: 0.86,
    maxDepth: 1.25,
    terrainWeights: [0.48, 0.18, 0.08, 0.05, 0.15, 0.06],
  },
  {
    name: 'ĐBSCL – khu vực Cần Thơ',
    center: [105.78, 10.045],
    region: 'south',
    gridCells: 44,
    floodIntensity: 0.8,
    maxDepth: 1.0,
    terrainWeights: [0.35, 0.35, 0.08, 0.02, 0.12, 0.08],
  },
  {
    name: 'Ven kênh Nhiêu Lộc – TP.HCM',
    center: [106.688, 10.785],
    region: 'south',
    gridCells: 48,
    floodIntensity: 0.83,
    maxDepth: 1.1,
    terrainWeights: [0.55, 0.12, 0.05, 0.03, 0.18, 0.07],
  },
  {
    name: 'Hải Phòng – ven sông Cấm',
    center: [106.68, 20.86],
    region: 'north',
    gridCells: 42,
    floodIntensity: 0.79,
    maxDepth: 1.05,
    terrainWeights: [0.5, 0.15, 0.08, 0.05, 0.17, 0.05],
  },
  {
    name: 'Vinh – ven sông Lam',
    center: [105.68, 18.68],
    region: 'central',
    gridCells: 42,
    floodIntensity: 0.84,
    maxDepth: 1.18,
    terrainWeights: [0.48, 0.18, 0.1, 0.05, 0.14, 0.05],
  },
  {
    name: 'Quảng Ngãi – trũng ven sông',
    center: [108.82, 15.12],
    region: 'central',
    gridCells: 40,
    floodIntensity: 0.88,
    maxDepth: 1.22,
    terrainWeights: [0.42, 0.22, 0.12, 0.05, 0.12, 0.07],
  },
  {
    name: 'Quy Nhơn – ven biển',
    center: [109.22, 13.78],
    region: 'central',
    gridCells: 40,
    floodIntensity: 0.81,
    maxDepth: 1.08,
    terrainWeights: [0.46, 0.2, 0.1, 0.05, 0.14, 0.05],
  },
  {
    name: 'Nha Trang – đô thị ven biển',
    center: [109.18, 12.24],
    region: 'central',
    gridCells: 40,
    floodIntensity: 0.78,
    maxDepth: 1.0,
    terrainWeights: [0.5, 0.16, 0.08, 0.05, 0.16, 0.05],
  },
  {
    name: 'Biên Hòa – Đồng Nai',
    center: [106.82, 10.95],
    region: 'south',
    gridCells: 44,
    floodIntensity: 0.82,
    maxDepth: 1.12,
    terrainWeights: [0.52, 0.14, 0.06, 0.03, 0.17, 0.08],
  },
  {
    name: 'Long Xuyên – An Giang',
    center: [105.38, 10.38],
    region: 'south',
    gridCells: 42,
    floodIntensity: 0.85,
    maxDepth: 1.15,
    terrainWeights: [0.38, 0.32, 0.1, 0.03, 0.12, 0.05],
  },
  {
    name: 'Buôn Ma Thuột – Tây Nguyên',
    center: [108.05, 12.68],
    region: 'highlands',
    gridCells: 40,
    floodIntensity: 0.76,
    maxDepth: 0.95,
    terrainWeights: [0.4, 0.15, 0.15, 0.12, 0.1, 0.08],
  },
  {
    name: 'Thái Nguyên – sông Cầu',
    center: [105.84, 21.58],
    region: 'north',
    gridCells: 40,
    floodIntensity: 0.74,
    maxDepth: 0.92,
    terrainWeights: [0.52, 0.18, 0.06, 0.05, 0.14, 0.05],
  },
];

/**
 * Miền căn chỉnh cho một vùng synthetic (theo tên trong {@link FLOOD_GRID_ZONES}) — dùng với {@link getDepthScaleForYear}.
 */
export function getFloodDemoSyntheticZoneCalibrationRegion(
  zoneName: string,
): FloodCalibrationZoneRegion {
  const z = FLOOD_GRID_ZONES.find((x) => x.name === zoneName);
  return z?.region ?? 'north';
}

/** Base rows for sidebar/KPI, aligned with {@link FLOOD_GRID_ZONES} map grid (before year factor in useFloodData). */
export interface FloodDemoSyntheticRegionRow {
  name: string;
  depthM: number;
  severity: string;
  status: 'warning' | 'severe';
  areaKm2: number;
  affectedPop: number;
}

/**
 * Camera target when sidebar selects a synthetic demo zone (no GeoJSON polygon).
 * Jitter matches {@link generateFloodGridData} so the yellow grid cluster sits in frame.
 */
export function getFloodDemoZoneFlyTo(
  zoneName: string,
  year: number,
): { longitude: number; latitude: number; zoom: number } | null {
  const zoneIdx = FLOOD_GRID_ZONES.findIndex((z) => z.name === zoneName);
  if (zoneIdx < 0) return null;
  const zone = FLOOD_GRID_ZONES[zoneIdx];
  const jitterLng = Math.sin(year * 3.1 + zoneIdx * 7.3) * 0.005;
  const jitterLat = Math.cos(year * 2.7 + zoneIdx * 5.1) * 0.005;
  return {
    longitude: zone.center[0] + jitterLng,
    latitude: zone.center[1] + jitterLat,
    zoom: 13.8,
  };
}

function syntheticGridCellsPerSideForZone(zoneGridCells: number): number {
  return Math.min(
    SYNTHETIC_GRID_MAX_CELLS_PER_SIDE,
    Math.round(zoneGridCells * SYNTHETIC_GRID_CELLS_FACTOR),
  );
}

export function getFloodDemoSyntheticRegionRows(): FloodDemoSyntheticRegionRow[] {
  return FLOOD_GRID_ZONES.map((z) => {
    const nSide = syntheticGridCellsPerSideForZone(z.gridCells);
    const sideKm = (nSide * FLOOD_GRID_CELL_SIZE_M) / 1000;
    const areaKm2 = Math.max(0.2, Math.round(sideKm * sideKm * 10) / 10);
    const severe = z.maxDepth >= 2 || z.floodIntensity >= 1.05;
    const severityLabel = z.maxDepth >= 3 ? 'Ngập nặng' : z.maxDepth >= 1.2 ? 'Cảnh báo' : 'Chú ý';
    const depthM = Math.round(z.maxDepth * 0.9 * 10) / 10;
    return {
      name: z.name,
      depthM,
      severity: severityLabel,
      status: severe ? 'severe' : 'warning',
      areaKm2,
      affectedPop: Math.max(800, Math.round(areaKm2 * 6200)),
    };
  });
}

const DEG_PER_CELL_LAT = FLOOD_GRID_CELL_SIZE_M / 111_320;

function degPerCellLng(lat: number) {
  return FLOOD_GRID_CELL_SIZE_M / (111_320 * Math.cos((lat * Math.PI) / 180));
}

/** 2D value-noise with smoothstep interpolation, tiling on [0,1). */
function createNoise2D(rand: () => number, res: number) {
  const g: number[][] = [];
  for (let i = 0; i <= res; i++) {
    g[i] = [];
    for (let j = 0; j <= res; j++) g[i][j] = rand();
  }
  return (nx: number, ny: number): number => {
    const x = (((nx % 1) + 1) % 1) * res;
    const y = (((ny % 1) + 1) % 1) * res;
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const fx = (x - xi) ** 2 * (3 - 2 * (x - xi));
    const fy = (y - yi) ** 2 * (3 - 2 * (y - yi));
    const clamp = (v: number) => Math.min(v, res);
    return (
      g[xi][yi] * (1 - fx) * (1 - fy) +
      g[clamp(xi + 1)][yi] * fx * (1 - fy) +
      g[xi][clamp(yi + 1)] * (1 - fx) * fy +
      g[clamp(xi + 1)][clamp(yi + 1)] * fx * fy
    );
  };
}

function fbm(noise: (x: number, y: number) => number, x: number, y: number, octaves = 4): number {
  let val = 0;
  let amp = 0.5;
  let freq = 1;
  let total = 0;
  for (let i = 0; i < octaves; i++) {
    val += noise(x * freq, y * freq) * amp;
    total += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return val / total;
}

/**
 * Seed **chỉ phụ thuộc ngày** (không theo giờ) — kéo scrub trong cùng một ngày sẽ giữ nguyên
 * bố cục lưới/noise; chỉ cường độ (hourScale·seasonalScale·stormScale) đổi theo giờ.
 * Giữ tên `floodDemoTimelineSeed` vẫn lấy cùng đầu vào; đổi thuật toán bỏ `hour`.
 */
function floodDemoSpatialSeed(iso: string | null | undefined): number {
  if (!iso) return 0;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 0;
  const d = new Date(t);
  const start = new Date(d.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((d.getTime() - start.getTime()) / 86400000);
  return dayOfYear * 7919 + d.getDate() * 31;
}

/**
 * Biên độ theo giờ (trong ngày) và theo ngày trong mùa lũ — kết hợp với {@link floodTimelineCalendar} (tháng 6–10).
 */
export function floodDemoTimeIntensityFactors(iso: string | null | undefined): {
  hourScale: number;
  seasonalScale: number;
} {
  if (!iso) return { hourScale: 1, seasonalScale: 1 };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { hourScale: 1, seasonalScale: 1 };
  const h = d.getHours();
  const hourScale = 0.72 + 0.38 * Math.sin((h / 24) * Math.PI * 2 - Math.PI / 2);
  const start = new Date(d.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((d.getTime() - start.getTime()) / 86400000);
  const seasonalRaw = 0.52 + 0.55 * Math.sin(((dayOfYear - 152) / 152) * Math.PI);
  const seasonalScale = Math.max(0.42, Math.min(1.38, seasonalRaw));
  return { hourScale, seasonalScale };
}

/**
 * Kết hợp hệ số theo giờ/mùa nền với kịch bản bão mô phỏng (cửa sổ 72h) khi scrub.
 */
export function resolveFloodIntensityFactors(
  iso: string | null | undefined,
  nowMs: number,
): { hourScale: number; seasonalScale: number; stormScale: number; inWindow: boolean } {
  const base = floodDemoTimeIntensityFactors(iso);
  return getDemoStormScales(iso, nowMs, base);
}

/**
 * Lưới ô trong toàn bộ polygon GeoJSON demo chỉ hiện khi zoom >= ngưỡng (tránh nặng khi nhìn xa).
 * Khi zoom thấp hơn nhưng có {@link generateFloodGridData} `selectedDemoRegionId` khớp tên feature,
 * vẫn sinh ô cho đúng polygon đó để chọn từ sidebar vẫn thấy dữ liệu.
 */
export const REGION_FLOOD_GRID_MIN_ZOOM = 11;

/**
 * Tham số mật độ lưới demo ({@link FLOOD_GRID_ZONES}): giảm lọc và phạt khoảng cách từ tâm
 * để phần lớn ô trong hình vuông zone vẫn hiển thị khi zoom thành phố.
 */
export const FLOOD_DEMO_GRID_DENSITY = {
  /** Tăng nhẹ ngưỡng để vùng rìa trông sạch hơn (loại bỏ vùng nước quá nông không đáng kể). */
  minRawDepthM: 0.075,
  /** Giảm phạt khoảng cách từ tâm → vùng “đầy” ô hơn quanh hành lang ngập. */
  distPenalty: 0.29,
  /** Đừng ép tất cả ô nông về 0,1 m — làm mất phân tầng màu trên lưới. */
  minDisplayedDepthM: 0.05,
  /**
   * Mép vùng synthetic: elip xoay (hành lang dài / dọc tuyến), không tròn đều.
   * Trục dài ~“dọc đường/sông”, trục ngắn ~bề rộng ngập; góc xoay phụ thuộc zone + năm.
   */
  organicCorridorSemiMajor: 0.97,
  organicCorridorSemiMinor: 0.42,
  organicEdgeWobble: 0.1,
} as const;

/** Số ô tối đa mỗi polygon vùng GeoJSON demo (macro) — tăng nhẹ để lưới trong polygon dày hơn. */
/** Giảm trần khi có nhiều polygon (toàn quốc) để giữ FPS. */
/** Tăng dần để lưới trong polygon GeoJSON (Hà Nội + toàn quốc) nhìn đặc hơn; vẫn có trần FPS. */
const REGION_DEMO_MAX_CELLS_PER_POLYGON = 1500;

/** Polygon có `properties.demo_dense_grid === true` — tăng trần số ô (cùng bước {@link FLOOD_GRID_CELL_SIZE_M} m). */
const REGION_DEMO_HIGH_DENSITY_MAX_CELLS = 9000;

function appendCellsFromRegionDemoPolygons(
  cells: FloodCell[],
  features: readonly Feature[] | null | undefined,
  year: number,
  selectedDateIso: string | null | undefined,
): void {
  if (!features?.length) return;
  const { hourScale, seasonalScale, stormScale } = resolveFloodIntensityFactors(
    selectedDateIso ?? null,
    getFloodScrubberNow().getTime(),
  );
  const tSeed = floodDemoSpatialSeed(selectedDateIso ?? null);
  const timeScale = hourScale * seasonalScale * stormScale;

  features.forEach((feature, fi) => {
    if (feature.geometry.type !== 'Polygon' && feature.geometry.type !== 'MultiPolygon') return;
    const props = feature.properties as {
      name?: string;
      depth_m?: number;
      demo_dense_grid?: boolean;
      calibration_region?: FloodCalibrationZoneRegion;
    } | null;
    if (!props?.name || props.depth_m == null || Number.isNaN(Number(props.depth_m))) return;

    const poly = feature as Feature<Polygon | MultiPolygon>;
    const box = turf.bbox(poly);
    const [minLng, minLat, maxLng, maxLat] = box;
    const areaM2 = turf.area(poly);
    if (areaM2 < 10) return;

    const maxCells = props.demo_dense_grid
      ? REGION_DEMO_HIGH_DENSITY_MAX_CELLS
      : REGION_DEMO_MAX_CELLS_PER_POLYGON;

    /** Cùng `depthM` với sidebar/KPI — tránh lệch màu lưới so với số ghi nhận. */
    const refDepthM = calibrateFloodRegionFromGeoJsonProperties(
      feature.properties as Record<string, unknown>,
      year,
    ).depthM;

    const stepLat = DEG_PER_CELL_LAT;

    const rand = seededRandom(year * 90917 + fi * 1009 + tSeed + 3);
    let added = 0;

    // Lưới tâm ô cách đều ~{@link FLOOD_GRID_CELL_SIZE_M} m (cùng công thức synthetic), không chia bbox theo nx/ny.
    for (let lat = minLat + stepLat * 0.5; lat <= maxLat && added < maxCells; lat += stepLat) {
      const stepLng = degPerCellLng(lat);
      for (let lng = minLng + stepLng * 0.5; lng <= maxLng && added < maxCells; lng += stepLng) {
        const pt = turf.point([lng, lat]);
        if (!turf.booleanPointInPolygon(pt, poly)) continue;

        const spatialRaw = 0.72 + 0.48 * Math.sin(lng * 820 + lat * 760 + fi * 1.7);
        const spatialNorm = Math.min(1, Math.max(0.22, (spatialRaw - 0.24) / 0.96));
        const noise = 0.35 + rand() * 0.65;
        const localBlend = Math.min(1, spatialNorm * noise * 1.08);
        const rawDepth = refDepthM * timeScale * localBlend;
        if (rawDepth < FLOOD_DEMO_GRID_DENSITY.minRawDepthM) continue;
        const peakForDay = refDepthM * timeScale;
        const capped = Math.min(rawDepth, peakForDay * 1.02);
        const safeCapped = Number.isFinite(capped)
          ? capped
          : FLOOD_DEMO_GRID_DENSITY.minDisplayedDepthM;
        const depth_m = Math.max(
          FLOOD_DEMO_GRID_DENSITY.minDisplayedDepthM,
          Math.round(safeCapped * 100) / 100,
        );

        const detail = rand();
        const velocity_ms = Math.round(Math.min(depth_m * 0.5 + detail * 0.4, 3.0) * 100) / 100;
        const risk_level = (
          depth_m > 3 ? 5 : depth_m > 2 ? 4 : depth_m > 1 ? 3 : depth_m > 0.5 ? 2 : 1
        ) as FloodCell['risk_level'];
        const confidence = Math.round((0.68 + rand() * 0.3) * 100) / 100;
        const elevation_m = Math.round((1.2 + detail * 3) * 10) / 10;

        cells.push({
          position: [lng, lat],
          depth_m,
          velocity_ms,
          risk_level,
          terrain: 'urban',
          confidence,
          elevation_m,
          zone_name: props.name,
        });
        added += 1;
      }
    }
  });
}

/**
 * Generate per-cell flood analysis for configured demo zones (per-zone square grid).
 * Each year produces a distinctly different flood pattern via:
 *  - sinusoidal intensity curve (some years mild, some catastrophic)
 *  - year-dependent noise offset (flood shape shifts)
 *  - zone center jitter (flood epicenter moves ~500 m)
 *  - per-zone activation varies by year
 *
 * Khi có `selectedDateIso`, lưới và cường độ thay đổi theo ngày/giờ (đồng bộ với thanh thời gian).
 *
 * @param selectedDemoRegionId — Khi zoom < {@link REGION_FLOOD_GRID_MIN_ZOOM}, chỉ sinh ô polygon
 *   trùng tên vùng này (sidebar). Tránh không có ô nào khớp `zone_name` khi đang làm mờ lưới synthetic.
 */
export function generateFloodGridData(
  year: number,
  selectedDateIso?: string | null,
  regionDemoFeatures?: readonly Feature[] | null,
  mapZoom?: number | null,
  selectedDemoRegionId?: string | null,
): FloodCell[] {
  const tSeed = floodDemoSpatialSeed(selectedDateIso ?? null);
  const rand = seededRandom(year * 54321 + 13 + tSeed);
  const { hourScale, seasonalScale, stormScale } = resolveFloodIntensityFactors(
    selectedDateIso ?? null,
    getFloodScrubberNow().getTime(),
  );
  const timeScale = hourScale * seasonalScale * stormScale;
  const cells: FloodCell[] = [];

  const yearNoiseOffX = (year - 2017) * 0.37 + (tSeed % 2000) * 1e-4;
  const yearNoiseOffY = (year - 2017) * 0.23 + ((tSeed >> 5) % 2000) * 1e-4;

  for (let zoneIdx = 0; zoneIdx < FLOOD_GRID_ZONES.length; zoneIdx++) {
    const zone = FLOOD_GRID_ZONES[zoneIdx];

    const depthNoise = createNoise2D(rand, 16);
    const terrainNoise = createNoise2D(rand, 8);
    const detailNoise = createNoise2D(rand, 24);

    // Zone center jitter per year (~500 m shift)
    const jitterLng = Math.sin(year * 3.1 + zoneIdx * 7.3) * 0.005;
    const jitterLat = Math.cos(year * 2.7 + zoneIdx * 5.1) * 0.005;
    const cLng = zone.center[0] + jitterLng;
    const cLat = zone.center[1] + jitterLat;

    const n = syntheticGridCellsPerSideForZone(zone.gridCells);
    const halfN = n / 2;
    const dLat = DEG_PER_CELL_LAT;
    const dLng = degPerCellLng(cLat);

    const zRegion = zone.region ?? 'north';
    const regional = getRegionWeightForYear(year, zRegion);
    const national = getNationalFloodIntensityFactor(year);
    const depthScale = getDepthScaleForYear(year, zRegion);
    const yearFactor =
      national * regional * (0.88 + rand() * 0.24) * hourScale * seasonalScale * stormScale;

    const cumWeights: number[] = [];
    let sum = 0;
    for (const w of zone.terrainWeights) {
      sum += w;
      cumWeights.push(sum);
    }

    for (let row = 0; row < n; row++) {
      for (let col = 0; col < n; col++) {
        const cellRand = rand();

        const nx = col / n;
        const ny = row / n;
        const dx = (col - halfN) / halfN;
        const dy = (row - halfN) / halfN;
        const dist = Math.sqrt(dx * dx + dy * dy);

        // Year-offset shifts the noise landscape so flood shape differs each year
        const noiseVal = fbm(depthNoise, nx + yearNoiseOffX, ny + yearNoiseOffY, 4);
        const detail = detailNoise((nx + yearNoiseOffX) * 3, (ny + yearNoiseOffY) * 3);

        const theta = zoneIdx * 2.19 + year * 0.43;
        const cosT = Math.cos(theta);
        const sinT = Math.sin(theta);
        const u = dx * cosT + dy * sinT;
        const v = -dx * sinT + dy * cosT;
        const wob = detail * FLOOD_DEMO_GRID_DENSITY.organicEdgeWobble + (noiseVal - 0.5) * 0.065;

        /**
         * Realism: Co giãn phạm vi ngập vật lý (spatial extent) theo cường độ thời gian.
         * Khi intensity thấp (nước rút), các ô ở rìa sẽ bị loại bỏ trước do ellipse co lại.
         */
        const extentScale = 0.65 + 0.35 * Math.min(1.2, timeScale);
        const a = (FLOOD_DEMO_GRID_DENSITY.organicCorridorSemiMajor + wob * 0.45) * extentScale;
        const b =
          (FLOOD_DEMO_GRID_DENSITY.organicCorridorSemiMinor + Math.abs(wob) * 0.28) * extentScale;
        if ((u * u) / (a * a) + (v * v) / (b * b) > 1) continue;

        const core =
          noiseVal * zone.floodIntensity -
          dist * FLOOD_DEMO_GRID_DENSITY.distPenalty +
          detail * 0.26;
        const cellJitter = 0.58 + cellRand * 0.68;
        const rawDepth = zone.maxDepth * yearFactor * core * cellJitter * depthScale;

        if (rawDepth < FLOOD_DEMO_GRID_DENSITY.minRawDepthM) continue;

        const cappedDepth = Math.min(rawDepth, zone.maxDepth * depthScale);
        const depth_m = Math.max(
          FLOOD_DEMO_GRID_DENSITY.minDisplayedDepthM,
          Math.round(cappedDepth * 100) / 100,
        );
        const lng = cLng + (col - halfN) * dLng;
        const lat = cLat + (row - halfN) * dLat;

        const tVal = (terrainNoise(nx * 2, ny * 2) + cellRand * 0.2) % 1;
        let tIdx = 0;
        for (let t = 0; t < cumWeights.length; t++) {
          if (tVal <= cumWeights[t]) {
            tIdx = t;
            break;
          }
        }

        const velocity_ms = Math.round(Math.min(depth_m * 0.5 + detail * 0.8, 3.0) * 100) / 100;
        const risk_level = (
          depth_m > 3 ? 5 : depth_m > 2 ? 4 : depth_m > 1 ? 3 : depth_m > 0.5 ? 2 : 1
        ) as FloodCell['risk_level'];
        const confidence = Math.round(Math.min(0.98, 0.65 + (1 - dist) * 0.35) * 100) / 100;
        const elevation_m = Math.round((1.5 + dist * 10 + noiseVal * 2) * 10) / 10;

        cells.push({
          position: [lng, lat],
          depth_m,
          velocity_ms,
          risk_level,
          terrain: TERRAIN_TYPES[tIdx],
          confidence,
          elevation_m,
          zone_name: zone.name,
        });
      }
    }
  }

  let polygonFeatures = regionDemoFeatures;
  /** Zoom thấp: chỉ thu hẹp theo vùng đang chọn (hiệu năng). Không chọn → vẫn sinh ô từ toàn bộ polygon GeoJSON để lưới theo bước ô đã cấu hình luôn thấy. */
  if (
    mapZoom != null &&
    mapZoom < REGION_FLOOD_GRID_MIN_ZOOM &&
    selectedDemoRegionId &&
    regionDemoFeatures?.length
  ) {
    const filtered = regionDemoFeatures.filter(
      (f) => (f.properties as { name?: string } | null)?.name === selectedDemoRegionId,
    );
    if (filtered.length > 0) polygonFeatures = filtered;
  }
  if (polygonFeatures?.length) {
    appendCellsFromRegionDemoPolygons(cells, polygonFeatures, year, selectedDateIso ?? null);
  }

  return cells;
}
